import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * @typedef {'closed' | 'opening' | 'open' | 'closing'} DrawerState
 *
 * @typedef {object} Transition
 * @property {DrawerState} state - The current state of the details element.
 * @property {number} generation - Incremented on every transition so deferred callbacks can detect staleness.
 */

/**
 * A custom element that manages the main menu drawer.
 *
 * This component owns the `open` attribute outright: summary activation is prevented so the
 * browser never toggles `<details>` in parallel with the transitions below. Deferred work is
 * tagged with a generation number and discarded when a newer transition has started, which is
 * what keeps rapid clicking from leaving the icon and the panel in disagreement.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
/**
 * Upper bound for a drawer transition. `Animation.finished` never settles when the tab is
 * hidden or the transition is interrupted, which would otherwise strand the drawer with
 * `open` still set and the page still scroll-locked.
 */
const TRANSITION_TIMEOUT = 700;

class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {WeakMap<HTMLDetailsElement, Transition>} */
  #transitions = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    const { state } = this.#transition(this.refs.details);

    return state === 'open' || state === 'opening';
  }

  /**
   * Get the tracked transition for a details element, seeding it from the DOM on first use.
   * @param {HTMLDetailsElement} details
   * @returns {Transition}
   */
  #transition(details) {
    let transition = this.#transitions.get(details);

    if (!transition) {
      transition = { state: details.hasAttribute('open') ? 'open' : 'closed', generation: 0 };
      this.#transitions.set(details, transition);
    }

    return transition;
  }

  /**
   * Invalidate any in-flight transition for a details element and start a new one.
   * @param {HTMLDetailsElement} details
   * @returns {Transition}
   */
  #beginTransition(details) {
    const transition = this.#transition(details);

    transition.generation++;

    return transition;
  }

  /**
   * Run a callback once the element's transition finishes, or once the timeout expires —
   * whichever comes first. Skipped entirely if a newer transition has since started.
   *
   * @param {Element} element
   * @param {Transition} transition
   * @param {number} generation
   * @param {() => void} callback
   */
  #onSettled(element, transition, generation, callback) {
    let settled = false;

    const run = () => {
      if (settled) return;
      settled = true;

      if (transition.generation !== generation) return;

      callback();
    };

    onAnimationEnd(element, run, { subtree: false });
    setTimeout(run, TRANSITION_TIMEOUT);
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Suppress the native <details> toggle so this component stays the only writer of `open`.
    event?.preventDefault();

    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    event?.preventDefault();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    const transition = this.#beginTransition(details);
    const { generation } = transition;

    transition.state = 'opening';
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details, generation);

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    // Opening writes the whole DOM state synchronously. Deferring the class to a frame
    // later would leave `open` set but unpaired if a close superseded it in between,
    // which is what stranded the drawer as a close icon over a hidden panel. Flushing
    // layout here gives the transition its closed starting styles instead.
    if (drawer instanceof HTMLElement) void drawer.offsetWidth;

    details.classList.add('menu-open');

    if (target) {
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
    }

    // Wait for the drawer animation to complete before trapping focus
    this.#onSettled(drawer || details, transition, generation, () => {
      transition.state = 'open';
      trapFocus(details);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const transition = this.#beginTransition(details);
    const { generation } = transition;

    transition.state = 'closing';
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    this.#onSettled(drawer || details, transition, generation, () => {
      // `open` is dropped only once the exit transition has finished, otherwise the browser
      // hides the panel mid-animation.
      transition.state = 'closed';
      details.removeAttribute('open');

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach((element) => this.#reset(element));
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Reset a details element to its closed state without animating
   *
   * @param {Element} element
   */
  #reset(element) {
    if (!(element instanceof HTMLDetailsElement)) return;

    const transition = this.#beginTransition(element);

    transition.state = 'closed';
    element.classList.remove('menu-open');
    element.removeAttribute('open');
    element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   * @param {number} [generation] - The transition that scheduled this, so a superseded one leaves the class alone
   */
  preventInitialAccordionAnimations(details, generation) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      if (generation !== undefined && this.#transition(details).generation !== generation) return;

      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
